import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, Download, RefreshCcw, IndianRupee, Package, Upload as UploadIcon, FileText } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const PRINT_STATUSES = ["Request received", "Quote shared", "Payment pending", "Payment received", "Proof approval", "Printing started", "Binding in process", "Packed", "Shipped", "Delivered"];
const ACADEMIC_STATUSES = ["Request received", "Quote shared", "Payment pending", "Payment received", "Work started", "Draft in progress", "Quality check", "Draft shared", "Revision requested", "Final document delivered", "Printing started", "Binding in process", "Packed", "Shipped", "Delivered"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab === "print") params.order_type = "print";
      if (tab === "academic") params.order_type = "academic";
      const [s, o] = await Promise.all([api.get("/admin/stats"), api.get("/admin/orders", { params })]);
      setStats(s.data);
      setOrders(o.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab]);

  return (
    <div data-testid="admin-dashboard" className="container-app py-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="section-eyebrow">Admin</span>
          <h1 className="font-display text-4xl text-navy mt-2">Control Room</h1>
          <div className="text-sm text-slate-500">Signed in as {user?.email}</div>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/articles" className="btn-outline" data-testid="admin-articles-link"><FileText className="w-4 h-4" /> Articles</Link>
          <button onClick={load} className="btn-outline"><RefreshCcw className="w-4 h-4" /> Refresh</button>
          <button onClick={logout} className="btn-outline">Log out</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mt-6">
        <Kpi icon={Package} label="Total" value={stats?.total_orders ?? "…"} />
        <Kpi label="Printing" value={stats?.print_orders ?? "…"} />
        <Kpi label="Academic" value={stats?.academic_orders ?? "…"} />
        <Kpi label="Pending" value={stats?.pending_orders ?? "…"} />
        <Kpi label="Delivered" value={stats?.delivered_orders ?? "…"} />
        <Kpi icon={IndianRupee} label="Revenue" value={stats ? `₹${stats.revenue}` : "…"} />
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b border-slate-200">
        {[["all", "All orders"], ["print", "Printing"], ["academic", "Academic"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? "border-navy text-navy" : "border-transparent text-slate-500 hover:text-navy"}`}>{l}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>
      ) : (
        <div className="mt-6 space-y-4">
          {orders.length === 0 && <div className="text-slate-500 text-sm">No orders yet.</div>}
          {orders.map((o) => (<OrderRow key={o.id} order={o} onRefresh={load} />))}
        </div>
      )}
    </div>
  );
}

function OrderRow({ order, onRefresh }) {
  const [status, setStatus] = useState(order.status);
  const [note, setNote] = useState("");
  const [quote, setQuote] = useState(order.quotation || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const statusesList = order.type === "print" ? PRINT_STATUSES : ACADEMIC_STATUSES;

  const updateStatus = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${order.id}/status`, { status, note });
      toast.success("Status updated");
      setNote("");
      onRefresh();
    } catch (e) { toast.error("Failed"); } finally { setSaving(false); }
  };

  const setQuotation = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${order.id}/quotation`, { amount: parseFloat(quote), note: "" });
      toast.success("Quotation set");
      onRefresh();
    } catch { toast.error("Failed"); } finally { setSaving(false); }
  };

  const markPaid = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${order.id}/payment`, { payment_status: "paid" });
      toast.success("Marked paid");
      onRefresh();
    } catch { toast.error("Failed"); } finally { setSaving(false); }
  };

  const downloadFile = async (fid, filename) => {
    try {
      const resp = await api.get(`/files/${fid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || fid; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const uploadDeliverable = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.id);
      }
      const combined = [...(order.deliverables || []), ...uploaded];
      await api.patch(`/admin/orders/${order.id}/deliverables`, { file_ids: combined, note: "Final document uploaded" });
      toast.success("Deliverable uploaded and shared with customer");
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div data-testid={`admin-order-${order.order_number}`} className="border border-slate-200 rounded-sm bg-white p-5">
      <div className="grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-3">
          <div className="section-eyebrow">{order.type === "print" ? "Printing" : "Academic"}</div>
          <div className="font-display text-xl text-navy mt-1">{order.order_number}</div>
          <div className="text-xs text-slate-500">{new Date(order.created_at).toLocaleString("en-IN")}</div>
          <div className="text-xs text-slate-700 mt-2">
            <div>{order.customer?.name}</div>
            <div>{order.customer?.mobile} · {order.customer?.email}</div>
          </div>
        </div>
        <div className="lg:col-span-4 text-sm text-slate-700">
          <div className="section-eyebrow mb-1">Details</div>
          {order.type === "print" ? (
            <ul className="text-xs space-y-1 text-slate-600">
              <li>Pages: {order.details.total_pages} ({order.details.bw_pages} B/W · {order.details.color_pages} colour)</li>
              <li>Copies: {order.details.copies} · {order.details.paper_size} {order.details.paper_gsm}</li>
              <li>Binding: {order.details.binding} · Delivery: {order.details.delivery}</li>
              <li>Address: {order.details.address} — {order.details.pincode}</li>
            </ul>
          ) : (
            <ul className="text-xs space-y-1 text-slate-600">
              <li>Service: {order.details.service_type}</li>
              <li>Topic: {order.details.topic}</li>
              <li>Deadline: {order.details.deadline || "—"}</li>
              <li>Language: {order.details.language} · {order.details.citation_style}</li>
            </ul>
          )}
          {(order.details.file_ids || []).length > 0 && (
            <div className="mt-3">
              <div className="section-eyebrow mb-1">Files</div>
              <div className="flex flex-wrap gap-2">
                {order.details.file_ids.map((fid) => (
                  <button key={fid} onClick={() => downloadFile(fid, `${order.order_number}-${fid.slice(0, 6)}`)}
                          className="inline-flex items-center gap-1 text-xs border border-slate-200 rounded-sm px-2 py-1 hover:border-navy">
                    <Download className="w-3 h-3" /> {fid.slice(0, 8)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="lg:col-span-5 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Update status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9" data-testid={`admin-status-${order.order_number}`}><SelectValue /></SelectTrigger>
                <SelectContent>{statusesList.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Courier: XXXX-YYYY" className="h-9" />
            </div>
            <div className="col-span-2 flex gap-2">
              <button onClick={updateStatus} disabled={saving} data-testid={`admin-status-btn-${order.order_number}`} className="btn-primary !py-2 !px-3 !text-xs">Save status</button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 items-end">
            <div>
              <Label className="text-xs">Quotation (₹)</Label>
              <Input type="number" value={quote} onChange={(e) => setQuote(e.target.value)} className="h-9" />
            </div>
            <div className="flex gap-2">
              <button onClick={setQuotation} disabled={saving} className="btn-outline !py-2 !text-xs">Set quote</button>
              <button onClick={markPaid} disabled={saving} className="btn-gold !py-2 !text-xs">Mark paid</button>
            </div>
          </div>
          <div className="text-xs text-slate-500">Current: <span className="font-medium text-navy">{order.status}</span> · Payment: <span className="font-medium">{order.payment_status}</span> · Quote: <span className="font-mono">{order.quotation ? `₹${order.quotation}` : "—"}</span></div>

          <div className="pt-3 border-t border-slate-100">
            <div className="section-eyebrow mb-2">Final deliverables ({(order.deliverables || []).length})</div>
            {(order.deliverables || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {order.deliverables.map((fid) => (
                  <button key={fid} onClick={() => downloadFile(fid, `${order.order_number}-final`)}
                          className="inline-flex items-center gap-1 text-xs border border-gold rounded-sm px-2 py-1 text-navy bg-gold/10 hover:bg-gold/20">
                    <Download className="w-3 h-3" /> {fid.slice(0, 8)}
                  </button>
                ))}
              </div>
            )}
            <label className="inline-flex items-center gap-2 text-xs border border-slate-200 rounded-sm px-2 py-1 cursor-pointer hover:border-navy">
              <UploadIcon className="w-3 h-3" />
              {uploading ? "Uploading…" : "Upload final document"}
              <input ref={fileInputRef} type="file" onChange={uploadDeliverable} accept=".pdf,.doc,.docx" className="hidden" data-testid={`admin-deliverable-${order.order_number}`} />
            </label>
          </div>

          {(order.revision_requests || []).length > 0 && (
            <div className="pt-3 border-t border-slate-100 text-xs">
              <div className="section-eyebrow mb-1">Revision requests</div>
              {order.revision_requests.map((r) => (
                <div key={r.id} className="text-slate-700">• {r.reason} <span className="text-slate-400">({new Date(r.at).toLocaleDateString("en-IN")})</span></div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="border border-slate-200 rounded-sm p-4 bg-white">
      <div className="flex items-center justify-between">
        <span className="section-eyebrow">{label}</span>
        {Icon ? <Icon className="w-4 h-4 text-gold" /> : null}
      </div>
      <div className="font-display text-2xl text-navy mt-2">{value}</div>
    </div>
  );
}
